import { notification } from 'antd';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { leasingApi } from '@/components/App/Leasing/api';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';

import { refreshOrderData } from '../../../store';

type TProps = {
  offerUuid: string;
  onClose: () => void;
};

const useApplicationForm = ({ offerUuid, onClose }: TProps) => {
  const dispatch = useAppDispatch();

  const { request } = useAppSelector((state) => state.llpLeasingOrder);
  const offer = request?.responses.find((item) => item.uuid === offerUuid);

  const [saveLoading, setSaveLoading] = useState(false);

  const {
    control,
    formState: { errors },
    reset,
    getValues,
    handleSubmit
  } = useForm<TApplicationForm>({ mode: 'all' });
  const isAlternative = useWatch({ control, name: 'isAlternative' });
  const bankIcon = offer?.leasingCompany.icon;

  useEffect(() => {
    if (!offer) return;
    reset({
      status: offer.status.uuid,
      comment: undefined,
      isAlternative: offer.isAlternativeOffer,
      acceptedAmount: offer.approvedAmount || request?.totalAmount || 0,
      initialPayment: offer.initialPayment || request?.initialPayment || 0
    });
  }, [offer]);

  const handleSave = handleSubmit(async (values: TApplicationForm) => {
    if (!request) {
      notification.error({
        title:
          'Неверный идентификатор лизинговой компании. Пожалуйста, выберите лизинговую компанию'
      });
      onClose();
      return;
    }
    setSaveLoading(true);

    try {
      await leasingApi.editLeasingOffer(offerUuid, {
        comment: values.comment,
        isAlternativeOffer: values.isAlternative,
        approvedAmount: values.acceptedAmount,
        initialPayment: values.initialPayment,
        additionalFiles: values.additionalDocument?.base64
          ? [values.additionalDocument.base64]
          : [],
        ...(offer?.status.uuid !== values.status && {
          status_uuid: values.status
        })
      });

      notification.success({
        title: 'Изменения успешно сохранены!'
      });
      dispatch(refreshOrderData());
      reset({ comment: '' });
      onClose();
    } catch (error) {
      handleError(error, {
        customTitle:
          'Не получилось сохранить изменения, пожалуйста попробуйте позже'
      });
    }

    setSaveLoading(false);
  });

  const handleResetPrices = (checked: boolean) => {
    if (checked) {
      reset({
        ...getValues(),
        acceptedAmount: offer?.approvedAmount || 0,
        initialPayment: offer?.initialPayment || 0
      });
      return;
    }
    reset({
      ...getValues(),
      acceptedAmount: request?.totalAmount || 0,
      initialPayment: request?.initialPayment || 0
    });
  };

  return {
    control,
    errors,
    saveLoading,
    isAlternative,
    bankIcon,
    handleResetPrices,
    reset,
    handleSave
  };
};

export default useApplicationForm;

type TApplicationForm = {
  status: string;
  comment: string;
  isAlternative: boolean;
  acceptedAmount: number;
  initialPayment: number;
  additionalDocument: {
    name?: string;
    base64?: string;
    file?: File;
  };
};
